// The countdown mixtape: a finished day's top songs played back to back from #N to #1, with the
// day's break bar between them — exactly like the River. It uses the engine's world mode on a
// private lineup: the day's sounds, and track i (in play order) on slot i, fed a few slots ahead.
// The engine switches songs on the bar, with each song's own mix, sounds and loudness gain.
import { create } from "zustand";
import { SLOT_BARS, type LoopData } from "@eb/shared";
import { clock } from "../stream";
import type { EngineHost } from "../audio/host";
import { riverPlayer, stopPreview, stopRiverIfPlaying } from "../river/context";

const { dayStartBar } = clock;

export interface Track {
  rank: number;
  loopId: string;
  title: string | null;
  author: string | null;
  likes: number;
  gain: number;
}

interface MixtapeState {
  playing: boolean;
  starting: boolean;
  day: number | null;
  /** Position in play order (0 = the first track = the lowest rank). */
  index: number;
  /** Share of the current song played (0–1). */
  progress: number;
  tracks: Track[];
  error: string;
}

export const useMixtape = create<MixtapeState>(() => ({
  playing: false,
  starting: false,
  day: null,
  index: 0,
  progress: 0,
  tracks: [],
  error: "",
}));

const AHEAD = 3;
let host: EngineHost | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
const sent = new Set<number>();
let session = 0;

const firstBar = (day: number) => dayStartBar(day);

async function feed(day: number, tracks: Track[], from: number, mine: number): Promise<void> {
  const player = await riverPlayer();
  for (let i = from; i < Math.min(tracks.length, from + AHEAD + 1); i++) {
    if (sent.has(i) || mine !== session || !host) continue;
    const data: LoopData | undefined = await player.loop(tracks[i].loopId);
    if (!data || mine !== session || !host) continue;
    host.setLoop(day, i, data, tracks[i].gain);
    sent.add(i);
  }
}

/** Start (or jump to) track `index` of `tracks` (play order) of `day`. Call `engineHostNow` from
 *  the click handler, before any await (audio unlock). */
export async function playMixtape(
  hostPromise: Promise<EngineHost>,
  day: number,
  tracks: Track[],
  index = 0,
): Promise<void> {
  const mine = ++session;
  useMixtape.setState({ starting: true, error: "", day, tracks, index });
  try {
    const h = await hostPromise;
    await h.resume();
    await stopPreview();
    await stopRiverIfPlaying();
    if (mine !== session) return;
    host = h;
    const player = await riverPlayer();
    const spec = await player.spec(day, h);
    h.setPeriod(spec);
    sent.clear();
    await feed(day, tracks, index, mine);
    if (mine !== session) return;
    // Start a beat or so from now, on the track's first bar.
    const now = h.lastClock?.samplePos ?? 0;
    h.locate(firstBar(day) + index * SLOT_BARS, Math.round(now + 0.3 * h.sampleRate), clock.bpm);
    useMixtape.setState({ playing: true, starting: false, index, progress: 0 });
    if (timer) clearInterval(timer);
    timer = setInterval(() => follow(day, tracks, mine), 200);
  } catch (e) {
    useMixtape.setState({ starting: false, playing: false, error: e instanceof Error ? e.message : String(e) });
  }
}

function follow(day: number, tracks: Track[], mine: number): void {
  const c = host?.lastClock;
  if (!c || mine !== session || !c.world) return;
  const rel = c.worldBar - firstBar(day);
  const i = Math.floor(rel / SLOT_BARS);
  if (i >= tracks.length) {
    stopMixtape();
    return;
  }
  const s = useMixtape.getState();
  const progress = Math.max(0, Math.min(1, (rel - i * SLOT_BARS) / (SLOT_BARS - 1)));
  if (i !== s.index || Math.abs(progress - s.progress) > 0.01) useMixtape.setState({ index: Math.max(0, i), progress });
  void feed(day, tracks, Math.max(0, i), mine);
}

/** Stop the mixtape (the stop button, leaving the page). */
export function stopMixtape(): void {
  session++;
  if (timer) clearInterval(timer);
  timer = null;
  host?.worldStop();
  sent.clear();
  if (useMixtape.getState().playing || useMixtape.getState().starting)
    useMixtape.setState({ playing: false, starting: false, progress: 0 });
}
